import { __assign, __awaiter, __generator, __spreadArrays } from 'tslib';
import ReactDOM from 'react-dom';
import * as React from 'react';
import React__default from 'react';
import kebabCase from 'lodash/kebabCase';
import camelCase from 'lodash/camelCase';

var renderComponentAtRootNode = function (_a) {
    var root = _a.root, rehydrated = _a.rehydrated;
    if (!rehydrated || !root) {
        return Promise.resolve();
    }
    // unmount; it's possible that this was rehydrated previously.
    ReactDOM.unmountComponentAtNode(root);
    return Promise.resolve(
    // eslint-disable-next-line
    ReactDOM.render(rehydrated, root));
};

var CONTAINER_ATTRIBUTE = "data-react-from-markup-container";
var DATA_REHYDRATABLE = "data-rehydratable";
var DATA_PROP_PREFIX = "data-prop-";
var ERRORS = {
    NO_REHYDRATOR_NAME: "Rehydrator name is missing from element",
    NO_REHYDRATOR_FOUND: "No rehydrator found for type"
};

// Taken from react-dom: https://github.com/facebook/react/blob/aeda7b745d9c080150704feb20ea576238a1b9a1/packages/react-dom/src/shared/DOMProperty.js#L272
var booleanAttributes = [
    "allowfullscreen",
    "async",
    "autofocus",
    "autoplay",
    "controls",
    "default",
    "defer",
    "disabled",
    "formnovalidate",
    "hidden",
    "itemscope",
    "loop",
    "nomodule",
    "novalidate",
    "open",
    "playsinline",
    "readonly",
    "required",
    "reversed",
    "scoped",
    "seamless"
];

/**
 * * Keys should always be lowercase
 * * Don't include any properties where key === value. These will be handled automatically.
 */
var attributeToPropMap = {
    "accent-height": "accentHeight",
    acceptcharset: "acceptCharset",
    accesskey: "accessKey",
    "alignment-baseline": "alignmentBaseline",
    allowfullscreen: "allowFullScreen",
    allowreorder: "allowReorder",
    allowtransparency: "allowTransparency",
    "arabic-form": "arabicForm",
    attributename: "attributeName",
    attributetype: "attributeType",
    autocomplete: "autoComplete",
    autoplay: "autoPlay",
    autoreverse: "autoReverse",
    basefrequency: "baseFrequency",
    "baseline-shift": "baselineShift",
    baseprofile: "baseProfile",
    calcmode: "calcMode",
    "cap-height": "capHeight",
    cellpadding: "cellPadding",
    cellspacing: "cellSpacing",
    charset: "charSet",
    "class": "className",
    classid: "classID",
    "clip-path": "clipPath",
    "clip-rule": "clipRule",
    clippathunits: "clipPathUnits",
    "color-interpolation": "colorInterpolation",
    "color-interpolation-filters": "colorInterpolationFilters",
    "color-profile": "colorProfile",
    "color-rendering": "colorRendering",
    colspan: "colSpan",
    contenteditable: "contentEditable",
    contentscripttype: "contentScriptType",
    contentstyletype: "contentStyleType",
    contextmenu: "contextMenu",
    crossorigin: "crossOrigin",
    datetime: "dateTime",
    diffuseconstant: "diffuseConstant",
    "dominant-baseline": "dominantBaseline",
    edgemode: "edgeMode",
    "enable-background": "enableBackground",
    enctype: "encType",
    externalresourcesrequired: "externalResourcesRequired",
    "fill-opacity": "fillOpacity",
    "fill-rule": "fillRule",
    filterres: "filterRes",
    filterunits: "filterUnits",
    "flood-color": "floodColor",
    "flood-opacity": "floodOpacity",
    "font-family": "fontFamily",
    "font-size": "fontSize",
    "font-size-adjust": "fontSizeAdjust",
    "font-stretch": "fontStretch",
    "font-style": "fontStyle",
    "font-variant": "fontVariant",
    "font-weight": "fontWeight",
    "for": "htmlFor",
    formnovalidate: "formNoValidate",
    frameborder: "frameBorder",
    "glyph-name": "glyphName",
    "glyph-orientation-horizontal": "glyphOrientationHorizontal",
    "glyph-orientation-vertical": "glyphOrientationVertical",
    glyphref: "glyphRef",
    gradienttransform: "gradientTransform",
    gradientunits: "gradientUnits",
    "horiz-adv-x": "horizAdvX",
    "horiz-origin-x": "horizOriginX",
    hreflang: "hrefLang",
    httpequiv: "httpEquiv",
    "image-rendering": "imageRendering",
    kernelmatrix: "kernelMatrix",
    kernelunitlength: "kernelUnitLength",
    keypoints: "keyPoints",
    keysplines: "keySplines",
    keytimes: "keyTimes",
    lengthadjust: "lengthAdjust",
    "letter-spacing": "letterSpacing",
    "lighting-color": "lightingColor",
    limitingconeangle: "limitingConeAngle",
    "marker-end": "markerEnd",
    "marker-mid": "markerMid",
    "marker-start": "markerStart",
    markerheight: "markerHeight",
    markerunits: "markerUnits",
    markerwidth: "markerWidth",
    maskcontentunits: "maskContentUnits",
    maskunits: "maskUnits",
    maxlength: "maxLength",
    mediagroup: "mediaGroup",
    novalidate: "noValidate",
    numoctaves: "numOctaves",
    "overline-position": "overlinePosition",
    "overline-thickness": "overlineThickness",
    "paint-order": "paintOrder",
    "panose-1": "panose1",
    pathlength: "pathLength",
    patterncontentunits: "patternContentUnits",
    patterntransform: "patternTransform",
    patternunits: "patternUnits",
    "pointer-events": "pointerEvents",
    pointsatx: "pointsAtX",
    pointsaty: "pointsAtY",
    pointsatz: "pointsAtZ",
    preservealpha: "preserveAlpha",
    preserveaspectratio: "preserveAspectRatio",
    primitiveunits: "primitiveUnits",
    radiogroup: "radioGroup",
    readonly: "readOnly",
    referrerpolicy: "referrerPolicy",
    refx: "refX",
    refy: "refY",
    "rendering-intent": "renderingIntent",
    repeatcount: "repeatCount",
    repeatdur: "repeatDur",
    requiredextensions: "requiredExtensions",
    requiredfeatures: "requiredFeatures",
    rowspan: "rowSpan",
    "shape-rendering": "shapeRendering",
    specularconstant: "specularConstant",
    specularexponent: "specularExponent",
    spellcheck: "spellCheck",
    spreadmethod: "spreadMethod",
    srcdoc: "srcDoc",
    srcset: "srcSet",
    startoffset: "startOffset",
    stddeviation: "stdDeviation",
    stitchtiles: "stitchTiles",
    "stop-color": "stopColor",
    "stop-opacity": "stopOpacity",
    "strikethrough-position": "strikethroughPosition",
    "strikethrough-thickness": "strikethroughThickness",
    "stroke-dasharray": "strokeDasharray",
    "stroke-dashoffset": "strokeDashoffset",
    "stroke-linecap": "strokeLinecap",
    "stroke-linejoin": "strokeLinejoin",
    "stroke-miterlimit": "strokeMiterlimit",
    "stroke-opacity": "strokeOpacity",
    "stroke-width": "strokeWidth",
    surfacescale: "surfaceScale",
    systemlanguage: "systemLanguage",
    tabindex: "tabIndex",
    tablevalues: "tableValues",
    targetx: "targetX",
    targety: "targetY",
    "text-anchor": "textAnchor",
    "text-decoration": "textDecoration",
    "text-rendering": "textRendering",
    textlength: "textLength",
    "underline-position": "underlinePosition",
    "underline-thickness": "underlineThickness",
    "unicode-bidi": "unicodeBidi",
    "unicode-range": "unicodeRange",
    "units-per-em": "unitsPerEm",
    usemap: "useMap",
    "v-alphabetic": "vAlphabetic",
    "v-hanging": "vHanging",
    "v-ideographic": "vIdeographic",
    "v-mathematical": "vMathematical",
    "vector-effect": "vectorEffect",
    "vert-adv-y": "vertAdvY",
    "vert-origin-x": "vertOriginX",
    "vert-origin-y": "vertOriginY",
    viewbox: "viewBox",
    viewtarget: "viewTarget",
    "word-spacing": "wordSpacing",
    "writing-mode": "writingMode",
    "x-height": "xHeight",
    xchannelselector: "xChannelSelector",
    "xlink:actuate": "xlinkActuate",
    "xlink:arcrole": "xlinkArcrole",
    "xlink:href": "xlinkHref",
    "xlink:role": "xlinkRole",
    "xlink:show": "xlinkShow",
    "xlink:title": "xlinkTitle",
    "xlink:type": "xlinkType",
    "xml:base": "xmlBase",
    "xml:lang": "xmlLang",
    "xml:space": "xmlSpace",
    ychannelselector: "yChannelSelector",
    zoomandpan: "zoomAndPan"
};

// For information on defaultValue vs. value, defaultChecked vs. checked,
var specialElementHandlers = {
    input: function (el, attributes) {
        var toReturn = __assign({}, attributes);
        // Never pass value or checked. These props would switch React form
        // elements into controlled mode.
        if (typeof toReturn.checked !== "undefined") {
            delete toReturn.checked;
        }
        if (typeof toReturn.value !== "undefined") {
            delete toReturn.value;
        }
        // Use the DOM element properties instead of attributes. These may reflect
        // changes the user has already made before we could rehydrate.
        if (el.checked) {
            toReturn.defaultChecked = el.checked;
        }
        if (el.value) {
            toReturn.defaultValue = el.value;
        }
        return toReturn;
    },
    option: function (_, attributes) {
        var toReturn = __assign({}, attributes);
        // <option> elements will have their "selected" attribute hoisted to the
        // <select> element that contains them, as a defaultValue.
        if (typeof toReturn.selected !== "undefined") {
            delete toReturn.selected;
        }
        return toReturn;
    },
    select: function (el, attributes) {
        var toReturn = __assign({}, attributes);
        // Passing value would switch <select> into controlled mode.
        if (typeof toReturn.value !== "undefined") {
            delete toReturn.value;
        }
        // Use the DOM element properties instead of attributes. These may reflect
        // changes the user has already made before we could rehydrate.
        if (el.value) {
            toReturn.defaultValue = el.value;
        }
        return toReturn;
    },
    textarea: function (el, attributes) {
        var toReturn = __assign({}, attributes);
        // Passing value would switch <textarea> into controlled mode.
        if (typeof toReturn.value !== "undefined") {
            delete toReturn.value;
        }
        // Use the DOM element properties instead of attributes. These may reflect
        // changes the user has already made before we could rehydrate.
        if (el.value) {
            toReturn.defaultValue = el.value;
        }
        return toReturn;
    }
};

var mapAttributeToReact = function (attributeName) {
    return (attributeToPropMap[attributeName.toLowerCase()] || attributeName.toLowerCase());
};
var makeRefStyleHandler = function (styleString) {
    return function (ref) {
        if (ref !== null) {
            ref.setAttribute("style", styleString);
        }
    };
};
var bootstrapStyles = function (attributes) { return (__assign(__assign({}, attributes), { ref: makeRefStyleHandler(attributes.style), 
    // eslint-disable-next-line
    style: undefined })); };
var staticToReactElement = (function (el, recursor) { return __awaiter(void 0, void 0, void 0, function () {
    var tagName, attributes, handler, children;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                tagName = el.tagName.toLowerCase();
                attributes = {};
                if (el.hasAttributes()) {
                    attributes = Array.from(el.attributes)
                        .map(function (_a) {
                        var name = _a.name, value = _a.value;
                        if (booleanAttributes.includes(name) &&
                            value === "") {
                            // React, and JavaScript, view "" as falsy. However, an empty string on
                            // a DOM element's attribute makes it truthy. This compensates for the
                            // discrepancy.
                            return { name: name, value: true };
                        }
                        return { name: name, value: value };
                    })
                        .reduce(function (acc, _a) {
                        var _b;
                        var name = _a.name, value = _a.value;
                        return (__assign(__assign({}, acc), (_b = {}, _b[mapAttributeToReact(name)] = value, _b)));
                    }, {});
                    if ("style" in attributes) {
                        attributes = bootstrapStyles(attributes);
                    }
                    handler = specialElementHandlers[tagName];
                    if (handler) {
                        attributes = handler(el, attributes);
                    }
                }
                if (!(tagName !== "textarea" && el.childNodes)) return [3 /*break*/, 2];
                return [4 /*yield*/, Promise.all(Array.from(el.childNodes).map(recursor))];
            case 1:
                children = _a.sent();
                return [2 /*return*/, React.createElement.apply(React, __spreadArrays([tagName, attributes], children))];
            case 2: return [2 /*return*/, React.createElement(tagName, attributes)];
        }
    });
}); });

var convertText = function (el) {
    // Text node - no need to convert, just return the text.
    return el.data;
};
var convertElement = function (el, customElementHandler, recursor) { return __awaiter(void 0, void 0, void 0, function () { return __generator(this, function (_a) {
    switch (_a.label) {
        case 0: return [4 /*yield*/, customElementHandler(el)];
        case 1: 
        // If customElementHandler is truthy, use it; otherwise, just convert to a React element.
        return [2 /*return*/, (_a.sent()) || staticToReactElement(el, recursor)];
    }
}); }); };
var convertToReactEelement = function (el, customElementHandler
// eslint-disable-next-line
) { return __awaiter(void 0, void 0, void 0, function () {
    var recursor;
    return __generator(this, function (_a) {
        recursor = function (innerEl) {
            return convertToReactEelement(innerEl, customElementHandler);
        };
        if (el.nodeType === Node.TEXT_NODE) {
            return [2 /*return*/, convertText(el)];
        }
        if (el.nodeType === Node.ELEMENT_NODE) {
            return [2 /*return*/, convertElement(el, customElementHandler, recursor)];
        }
        /**
         * unhandled node type, cannot return null
         * WARNING: React components that may iterate over children will attempt to access
         * `child.type || child.props`, this catch all return may be a source of error
         */
        return [2 /*return*/, ""];
    });
}); };

var rehydrateChildren$1 = function (node, customHandler) { return __awaiter(void 0, void 0, void 0, function () {
    var children;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                if (!node || !node.childNodes) {
                    return [2 /*return*/, null];
                }
                else if (node.childNodes.length === 1) {
                    return [2 /*return*/, convertToReactEelement(node.childNodes[0], customHandler)];
                }
                return [4 /*yield*/, Promise.all(Array.from(node.childNodes).map(function (child) { return convertToReactEelement(child, customHandler); }))];
            case 1:
                children = _a.sent();
                return [2 /*return*/, React__default.createElement.apply(React__default, __spreadArrays([React__default.Fragment, {}], children))];
        }
    });
}); };

var rehydrateChildren = function (el, rehydrators, options) {
    // eslint-disable-next-line
    return rehydrateChildren$1(el, createCustomHandler(rehydrators, options));
};
var createCustomHandler = function (rehydrators, options) {
    var customHandler = function (node) {
        // This function will run on _every_ node that domElementToReact encounters.
        // Make sure to keep the conditional highly performant.
        if (node.nodeType === Node.ELEMENT_NODE &&
            node.hasAttribute(DATA_REHYDRATABLE)) {
            return rehydratableToReactElement(node, rehydrators, options);
        }
        return Promise.resolve(false);
    };
    return customHandler;
};
var rehydratableToReactElement = function (el, rehydrators, options) {
    var rehydratorName = el.getAttribute(DATA_REHYDRATABLE);
    if (!rehydratorName) {
        throw new TypeError(ERRORS.NO_REHYDRATOR_NAME);
    }
    var rehydrator = rehydrators[rehydratorName];
    if (!rehydrator) {
        throw new TypeError(ERRORS.NO_REHYDRATOR_FOUND + ": " + rehydratorName);
    }
    return rehydrator(el, function (children) {
        return rehydrateChildren(children, rehydrators, options);
    }, options.extra);
};

var rehydrateElementsAtRootNode = function (root, rehydrators, options) { return __awaiter(void 0, void 0, void 0, function () {
    var rehydrated;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, rehydrateChildren(root, rehydrators, options)];
            case 1:
                rehydrated = _a.sent();
                return [2 /*return*/, { root: root, rehydrated: rehydrated }];
        }
    });
}); };

// eslint-disable-next-line max-params
function rehydrate(container, rehydrators, options, renderer) {
    if (renderer === void 0) { renderer = renderComponentAtRootNode; }
    return __awaiter(this, void 0, void 0, function () {
        var containerElements, roots, rehydratedNodePromises, _i, roots_1, root, rehydratedNodes, renderedComponents;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    containerElements = container.querySelectorAll("[" + CONTAINER_ATTRIBUTE + "]");
                    roots = Array.from(
                    /** TODO: allow setting a container identifier so multiple rehydration instances can exist */
                    containerElements).reduce(function (acc, root) {
                        // filter roots that are contained within other roots
                        if (!acc.some(function (r) { return r.contains(root); })) {
                            acc.push(root);
                        }
                        return acc;
                    }, []);
                    rehydratedNodePromises = [];
                    for (_i = 0, roots_1 = roots; _i < roots_1.length; _i++) {
                        root = roots_1[_i];
                        // It's possible that this root was detached by a previous render in this loop
                        if (container.contains(root)) {
                            rehydratedNodePromises.push(rehydrateElementsAtRootNode(root, rehydrators, options));
                        }
                    }
                    return [4 /*yield*/, Promise.all(rehydratedNodePromises)];
                case 1:
                    rehydratedNodes = _a.sent();
                    return [4 /*yield*/, Promise.all(rehydratedNodes.map(renderer))];
                case 2:
                    renderedComponents = _a.sent();
                    return [2 /*return*/, renderedComponents];
            }
        });
    });
}

/* eslint-disable @typescript-eslint/ban-ts-comment */
// TODO: use type string literals
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function propsToDataAttributes(props) {
    return Object.entries(props).reduce(function (acc, _a) {
        var name = _a[0], value = _a[1];
        if (typeof value !== "string") {
            // eslint-disable-next-line
            value = JSON.stringify(value);
        }
        // @ts-ignore
        acc[DATA_PROP_PREFIX + kebabCase(name)] = value;
        return acc;
    }, {});
}
function elementAttributesToProps(el) {
    return el.getAttributeNames().reduce(function (acc, rawAttributeName) {
        var isProp = rawAttributeName.includes(DATA_PROP_PREFIX);
        if (isProp) {
            // eslint-disable-next-line
            var rawValue = el.getAttribute(rawAttributeName);
            var attributeName = camelCase(rawAttributeName.replace(DATA_PROP_PREFIX, ""));
            try {
                // @ts-ignore
                acc[attributeName] = JSON.parse(rawValue);
            }
            catch (err) {
                // @ts-ignore
                acc[attributeName] = rawValue;
            }
        }
        return acc;
    }, {});
}

var createRehydratableTest = function (markup, args) { return __awaiter(void 0, void 0, void 0, function () {
    var container, portals;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                container = document.createElement("div");
                container.innerHTML = "\n        <div " + CONTAINER_ATTRIBUTE + ">\n        " + markup + "\n        </div>\n    ";
                return [4 /*yield*/, rehydrate(container, args.rehydrators, args.options || { extra: {} }, function (_a) {
                        var root = _a.root, rehydrated = _a.rehydrated;
                        return Promise.resolve(ReactDOM.createPortal(rehydrated, root));
                    })];
            case 1:
                portals = (_a.sent());
                return [2 /*return*/, { markup: markup, portals: portals, container: container }];
        }
    });
}); };

export default rehydrate;
export { createCustomHandler, createRehydratableTest, elementAttributesToProps, propsToDataAttributes, rehydratableToReactElement, rehydrateChildren };
//# sourceMappingURL=index.esm.js.map
